import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  Alert,
  Spinner,
  Badge,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from '../shim/reactstrap';
import { useAuth } from '../contexts/AuthContext';
import { PortfolioHistoryChart } from './PortfolioHistoryChart';

// Read-only view page for a single Trading Strategy.
// URL: /trading-strategies/:id/view
// URL: /trading-strategies/:id/view/review-pending-trade  (opens approve/decline dialog)
export const TradingStrategyView = ({ reviewPendingTrade = false }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiClient, user, isAuthenticated } = useAuth();

  const [strategy, setStrategy] = useState(null);
  const [loadError, setLoadError] = useState('');

  const [portfolio, setPortfolio] = useState(null);
  const [portfolioError, setPortfolioError] = useState('');
  const [portfolioLoading, setPortfolioLoading] = useState(false);

  const [orders, setOrders] = useState(null);
  const [ordersError, setOrdersError] = useState('');
  const [ordersLoading, setOrdersLoading] = useState(false);

  const [tradeHistory, setTradeHistory] = useState(null);
  const [tradeHistoryError, setTradeHistoryError] = useState('');
  const [tradeHistoryLoading, setTradeHistoryLoading] = useState(false);

  // Pending trade review dialog
  const [pendingTradeModalOpen, setPendingTradeModalOpen] = useState(reviewPendingTrade);
  const [pendingTradeActionInProgress, setPendingTradeActionInProgress] = useState(false);
  const [pendingTradeError, setPendingTradeError] = useState('');

  const loadStrategy = useCallback(async (strategyId) => {
    setLoadError('');
    try {
      const s = await apiClient.getTradingStrategy(strategyId);
      setStrategy(s);
    } catch (err) {
      setLoadError(err?.message || 'Failed to load trading strategy.');
    }
  }, [apiClient]);

  const loadPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    setPortfolioError('');
    try {
      const data = await apiClient.getTradingStrategyPortfolio(id);
      setPortfolio(data);
    } catch (err) {
      setPortfolioError(err?.message || 'Failed to load portfolio.');
      setPortfolio(null);
    } finally {
      setPortfolioLoading(false);
    }
  }, [apiClient, id]);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    setOrdersError('');
    try {
      const data = await apiClient.getTradingStrategyOrders(id);
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      setOrdersError(err?.message || 'Failed to load open orders.');
      setOrders(null);
    } finally {
      setOrdersLoading(false);
    }
  }, [apiClient, id]);

  const loadTradeHistory = useCallback(async () => {
    setTradeHistoryLoading(true);
    setTradeHistoryError('');
    try {
      const data = await apiClient.getTradingStrategyTradeHistory(id, 50);
      setTradeHistory(Array.isArray(data) ? data : []);
    } catch (err) {
      setTradeHistoryError(err?.message || 'Failed to load trade history.');
      setTradeHistory(null);
    } finally {
      setTradeHistoryLoading(false);
    }
  }, [apiClient, id]);

  useEffect(() => {
    if (id) {
      loadStrategy(id);
      loadPortfolio();
      loadOrders();
      loadTradeHistory();
    }
  }, [id, loadPortfolio, loadStrategy, loadOrders, loadTradeHistory]);

  // Open the review modal automatically when navigated to the review URL and
  // the strategy has a pending trade. The modal is also gated on ownership —
  // admins viewing other users' strategies cannot approve / decline pending
  // trades, so we never auto-open the modal for them.
  const isOwner = isAuthenticated && user?.Id === strategy?.UserId;
  useEffect(() => {
    if (reviewPendingTrade && isOwner && strategy?.PendingTrade) {
      setPendingTradeModalOpen(true);
    }
  }, [reviewPendingTrade, isOwner, strategy]);

  const handleApprovePendingTrade = async () => {
    setPendingTradeActionInProgress(true);
    setPendingTradeError('');
    try {
      await apiClient.approvePendingTrade(id);
      setPendingTradeModalOpen(false);
      // Reload strategy and navigate away from review URL
      await loadStrategy(id);
      navigate(`/trading-strategies/${id}/view`, { replace: true });
    } catch (err) {
      setPendingTradeError(err?.message || 'Failed to approve the pending trade.');
    } finally {
      setPendingTradeActionInProgress(false);
    }
  };

  const handleDeclinePendingTrade = async () => {
    setPendingTradeActionInProgress(true);
    setPendingTradeError('');
    try {
      await apiClient.declinePendingTrade(id);
      setPendingTradeModalOpen(false);
      await loadStrategy(id);
      navigate(`/trading-strategies/${id}/view`, { replace: true });
    } catch (err) {
      setPendingTradeError(err?.message || 'Failed to decline the pending trade.');
    } finally {
      setPendingTradeActionInProgress(false);
    }
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3" style={{ gap: '12px' }}>
        <h1 className="mb-0">Trading Strategy #{id}</h1>
        {isAuthenticated && user?.Id === strategy?.UserId && (
          <Button color="primary" size="sm" tag={Link} to={`/trading-strategies/${id}/edit`}>
            Edit
          </Button>
        )}
        <Button color="secondary" size="sm" tag={Link} to="/trading-strategies">
          Back
        </Button>
      </div>

      {loadError && <Alert color="danger">{loadError}</Alert>}

      {/* Top-of-page "broker unavailable" banner. The backend's
          /portfolio endpoint returns Account = null whenever the broker
          rejected the account call — most commonly because the connector's
          API key has been deactivated (Alpaca returns 401 unauthorized
          once the account moves from ACCOUNT_CLOSED_PENDING to fully
          closed). Every other broker endpoint (positions, orders,
          trade-history, portfolio-history) is also affected, so we render
          a single prominent banner here rather than scattering partial
          messages below. */}
      {portfolio && !portfolio.Account && (
        <Alert color="warning" className="d-flex align-items-start" style={{ gap: '8px' }}>
          <div>
            <strong>Broker account is closed or its API key has been deactivated.</strong>
            <div className="text-muted small mt-1">
              {portfolio.Account && portfolio.Account.Status
                ? `Status reported by the broker: ${portfolio.Account.Status}. `
                : 'The broker did not return account details for this connector. '}
              Open positions, open orders, trade history, and portfolio history
              cannot be loaded until the account is reopened or this strategy is
              pointed at a different connector.
            </div>
          </div>
        </Alert>
      )}

      {!strategy && !loadError && (
        <div className="text-center py-3"><Spinner /></div>
      )}

      {strategy && (
        <table className="table" style={{ maxWidth: '720px' }}>
          <tbody>
            <tr>
              <th style={{ width: '220px' }}>Id</th>
              <td>{strategy.Id}</td>
            </tr>
            <tr>
              <th>Name</th>
              <td>{strategy.Name || <span className="text-muted">-</span>}</td>
            </tr>
            <tr>
              <th>Trading Connector</th>
              <td>
                {(() => {
                  const id = `#${strategy.TradingConnectorId}`;
                  const name = strategy.TradingConnectorName;
                  const type = strategy.TradingConnectorType;
                  const mode = strategy.TradingConnectorMode;
                  const meta = [type, mode].filter(Boolean).join(' / ');
                  if (name && meta) return `${id} ${name} [${meta}]`;
                  if (name) return `${id} ${name}`;
                  if (meta) return `${id} [${meta}]`;
                  return id;
                })()}
              </td>
            </tr>
            <tr>
              <th>Model</th>
              <td>
                {strategy.ModelName
                  ? `${strategy.ModelName} (#${strategy.ModelId})`
                  : `#${strategy.ModelId}`}
              </td>
            </tr>
            <tr>
              <th>Signal Tag</th>
              <td>
                {strategy.SignalTag
                  ? <span className="badge bg-info text-dark">{strategy.SignalTag}</span>
                  : <span className="text-muted">-</span>}
              </td>
            </tr>
            <tr>
              <th>Enabled</th>
              <td>
                {strategy.Enabled ? (
                  <Badge color="success">Enabled</Badge>
                ) : (
                  <Badge color="secondary">Disabled</Badge>
                )}
              </td>
            </tr>
            <tr>
              <th>Owner</th>
              <td>{strategy.OwnerName || <span className="text-muted">-</span>}</td>
            </tr>
            <tr>
              <th>Created</th>
              <td>{strategy.getFormattedCreateTime() || <span className="text-muted">-</span>}</td>
            </tr>
            <tr>
              <th>Updated</th>
              <td>{strategy.getFormattedUpdateTime() || <span className="text-muted">-</span>}</td>
            </tr>
            <tr>
              <th>Latest Signal</th>
              <td>
                {strategy.hasLatestSignal() ? (
                  <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <Badge color={strategy.getLatestSignalActionColor()}>
                      {strategy.getLatestSignalActionLabel()}
                    </Badge>
                    <span>{strategy.getFormattedLatestSignalDate()}</span>
                  </div>
                ) : (
                  <span className="text-muted">
                    No signal has been computed yet. Edit this strategy to recompute.
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th>Initial Signal</th>
              <td>
                {strategy.InitialSignalTraded ? (
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                    <Badge color="secondary">Acted on</Badge>
                    <span className="text-muted small">
                      The executor has already acted on the latest signal — it will
                      only trade again if the signal flips.
                    </span>
                  </div>
                ) : strategy.hasLatestSignal() ? (
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                    <Badge color="info">Pending</Badge>
                    <span className="text-muted small">
                      The next executor run will trade on the persisted
                      {' '}<Badge color={strategy.getLatestSignalActionColor()} className="align-middle">
                        {strategy.getLatestSignalActionLabel()}
                      </Badge>
                      {' '}signal, regardless of whether the new run's signal flips.
                    </span>
                  </div>
                ) : (
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                    <Badge color="warning">Pending (no signal)</Badge>
                    <span className="text-muted small">
                      The next executor run will record the freshly-computed
                      signal as the new baseline (no trade on the first run).
                    </span>
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <th>Trade Confirmation</th>
              <td>
                {strategy.RequireTradeConfirmation ? (
                  <Badge color="warning">Required</Badge>
                ) : (
                  <Badge color="secondary">Not required</Badge>
                )}
              </td>
            </tr>
            <tr>
              <th>Order Type</th>
              <td>
                {strategy.OrderType === 'Market' ? (
                  <div>
                    <Badge color="info">Market</Badge>
                    <span className="text-muted small d-block mt-1">
                      Fills immediately at the next available intraday price.
                    </span>
                  </div>
                ) : (
                  <div>
                    <Badge color="primary">Market-on-Close</Badge>
                    <span className="text-muted small d-block mt-1">
                      Fills at the official closing auction price for the day.
                    </span>
                  </div>
                )}
              </td>
            </tr>
            <tr>
              <th>Time of Execution</th>
              <td>
                <span>{strategy.ExecutionTime.substring(0, 5)} ET</span>
                <span className="text-muted small d-block mt-1">
                  Eastern time at which the scheduler triggers this strategy on trading days.
                </span>
              </td>
            </tr>
            <tr>
              <th>Trade Amount (USD)</th>
              <td>
                {strategy.TradeAmountUsd !== null && strategy.TradeAmountUsd !== undefined ? (
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                    <span>${Number(strategy.TradeAmountUsd).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                    {strategy.AdjustTradeAmountAutomatically ? (
                      <Badge color="info">Auto-adjust enabled</Badge>
                    ) : (
                      <Badge color="secondary">Fixed</Badge>
                    )}
                  </div>
                ) : (
                  <span className="text-muted">
                    Not set — executor deploys all available TradableCash.
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th>Last Trade Amount Adjustment</th>
              <td>
                {strategy.LastTradeAmountAdjustTime ? (
                  <span>{strategy.getFormattedLastAdjustTime()}</span>
                ) : (
                  <span className="text-muted">
                    Never — the adjuster has not yet written a new TradeAmountUsd
                    for this strategy. Stays null until the first scheduled or
                    manual run completes against an eligible strategy.
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th>Debugging</th>
              <td>
                {strategy.EnableDebugging ? (
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                    <Badge color="info">Enabled</Badge>
                    <span className="text-muted small">
                      Picked up by an admin's manual "Trading Strategy Execution"
                      trigger from the admin Schedulers page — but only when this
                      strategy is owned by the admin who clicked the button. The
                      daily 15:30 ET scheduler ignores this flag.
                    </span>
                  </div>
                ) : (
                  <span className="text-muted">
                    Not enabled. The manual admin "Trading Strategy Execution"
                    trigger skips this strategy.
                  </span>
                )}
              </td>
            </tr>
            <tr>
              <th>Pending Trade</th>
              <td>
                {strategy.PendingTrade ? (
                  <div className="d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
                    <Badge color={pendingActionColor(strategy.PendingTrade.Action)}>
                      {strategy.PendingTrade.Action}
                    </Badge>
                    <Badge color={strategy.getPendingTradeOrderTypeColor()}>
                      {strategy.getPendingTradeOrderTypeLabel()}
                    </Badge>
                    <span className="text-muted small">
                      Signal: {strategy.PendingTrade.SignalTime
                        ? strategy.PendingTrade.SignalTime.local().format('YYYY-MM-DD')
                        : '—'}
                    </span>
                    <span className="text-muted small">
                      Since: {strategy.PendingTrade.CreatedAt
                        ? strategy.PendingTrade.CreatedAt.local().format('L LTS')
                        : '—'}
                    </span>
                    {isOwner && (
                      <Button
                        color="warning"
                        size="sm"
                        onClick={() => setPendingTradeModalOpen(true)}
                      >
                        Review Pending Trade
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="d-flex align-items-center" style={{ gap: '8px' }}>
                    <span className="text-muted">None</span>
                    <Button color="warning" size="sm" disabled>
                      Review Pending Trade
                    </Button>
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {/* Pending trade review modal */}
      <Modal isOpen={pendingTradeModalOpen} toggle={() => !pendingTradeActionInProgress && setPendingTradeModalOpen(false)}>
        <ModalHeader toggle={() => !pendingTradeActionInProgress && setPendingTradeModalOpen(false)}>
          Review Pending Trade
        </ModalHeader>
        <ModalBody>
          {strategy?.PendingTrade ? (
            <>
              <p>
                The trading executor has detected a signal flip for strategy{' '}
                <strong>{strategy.Name || `#${id}`}</strong> and is waiting for your confirmation
                before placing any orders.
              </p>
              <table className="table table-sm" style={{ maxWidth: '400px' }}>
                <tbody>
                  <tr>
                    <th style={{ width: '140px' }}>Action</th>
                    <td>
                      <Badge color={pendingActionColor(strategy.PendingTrade.Action)}>
                        {strategy.PendingTrade.Action}
                      </Badge>
                    </td>
                  </tr>
                  <tr>
                    <th>Order Type</th>
                    <td>
                      <Badge color={strategy.getPendingTradeOrderTypeColor()}>
                        {strategy.getPendingTradeOrderTypeLabel()}
                      </Badge>
                      <span className="text-muted small d-block mt-1">
                        {strategy.getPendingTradeOrderTypeLabel() === 'Market-on-Close'
                          ? 'Will be submitted at the official closing auction price for the day.'
                          : 'Will fill at the next available intraday market price.'}
                      </span>
                    </td>
                  </tr>
                  <tr>
                    <th>Signal Date</th>
                    <td>
                      {strategy.PendingTrade.SignalTime
                        ? strategy.PendingTrade.SignalTime.local().format('YYYY-MM-DD')
                        : '—'}
                    </td>
                  </tr>
                  <tr>
                    <th>Pending Since</th>
                    <td>
                      {strategy.PendingTrade.CreatedAt
                        ? strategy.PendingTrade.CreatedAt.local().format('L LTS')
                        : '—'}
                    </td>
                  </tr>
                </tbody>
              </table>
              {pendingTradeError && <Alert color="danger" className="mt-2">{pendingTradeError}</Alert>}
            </>
          ) : (
            <p className="text-muted">No pending trade to review.</p>
          )}
        </ModalBody>
        <ModalFooter>
          {isOwner && (
            <>
              <Button
                color="success"
                disabled={pendingTradeActionInProgress || !strategy?.PendingTrade}
                onClick={handleApprovePendingTrade}
              >
                {pendingTradeActionInProgress ? <Spinner size="sm" /> : 'Approve'}
              </Button>
              <Button
                color="danger"
                disabled={pendingTradeActionInProgress || !strategy?.PendingTrade}
                onClick={handleDeclinePendingTrade}
              >
                {pendingTradeActionInProgress ? <Spinner size="sm" /> : 'Decline'}
              </Button>
            </>
          )}
          <Button
            color="secondary"
            disabled={pendingTradeActionInProgress}
            onClick={() => setPendingTradeModalOpen(false)}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      <h2 className="h4 mt-4 mb-3">Portfolio Construction</h2>

      {strategy && (
        <table className="table" style={{ maxWidth: '720px' }}>
          <tbody>
            <tr>
              <th style={{ width: '220px' }}>Money Market Ticker</th>
              <td>
                {strategy.MoneyMarketSymbol
                  ? <strong>{strategy.MoneyMarketSymbol}</strong>
                  : <span className="text-muted">Not set — Money signals liquidate to cash.</span>}
              </td>
            </tr>
          </tbody>
        </table>
      )}

      {strategy && (
        strategy.Stocks && strategy.Stocks.length > 0 ? (
          <Table striped responsive>
            <thead>
              <tr>
                <th>Symbol</th>
                <th style={{ width: '140px' }}>Share</th>
              </tr>
            </thead>
            <tbody>
              {strategy.Stocks.map((s) => (
                <tr key={s.Symbol}>
                  <td><strong>{s.Symbol}</strong></td>
                  <td>{formatStockShare(s.Percentage)}</td>
                </tr>
              ))}
              <tr>
                <th>Total</th>
                <td>
                  <Badge color={Math.abs(stockShareSum(strategy) - 100) < 0.01 ? 'success' : 'warning'}>
                    {formatStockShare(stockShareSum(strategy))}
                  </Badge>
                </td>
              </tr>
            </tbody>
          </Table>
        ) : (
          <p className="text-muted">
            No stocks configured. Edit this strategy to allocate stocks and their share.
          </p>
        )
      )}

      <h2 className="h4 mt-4 mb-3">Portfolio</h2>

      {portfolioError && <Alert color="danger">{portfolioError}</Alert>}

      {portfolioLoading && (
        <div className="text-center py-3"><Spinner /></div>
      )}

      {portfolio && !portfolioLoading && (
        <>
          {/* The backend returns Account = null when the broker (e.g. Alpaca)
              rejected the /v2/account call or returned a payload the SDK
              can't deserialise (e.g. a new AccountStatus value, or the
              account is genuinely closed). Surface this as a clear banner
              instead of silently hiding the whole account section — and
              also suppress the misleading numeric rows (equity / cash /
              buying power) since the broker wouldn't have given us those
              values for a closed account. */}
          {(() => {
            const status = portfolio.Account && portfolio.Account.Status;
            const isClosedAccount = !portfolio.Account ||
              status === 'AccountClosed' ||
              status === 'AccountClosedPending';
            if (!isClosedAccount) return null;
            return (
              <Alert color="warning" className="d-flex align-items-center" style={{ gap: '8px' }}>
                <strong>Broker account is closed.</strong>
                <span className="text-muted small">
                  {status
                    ? `Status reported by the broker: ${status}.`
                    : 'The broker did not return account details for this connector.'}
                  {' '}Trading and balance data cannot be loaded until the account is reopened or
                  this strategy is pointed at a different connector.
                </span>
              </Alert>
            );
          })()}

          {portfolio.Account && portfolio.Account.Status !== 'AccountClosed' &&
            portfolio.Account.Status !== 'AccountClosedPending' && (
            <table className="table" style={{ maxWidth: '720px' }}>
              <tbody>
                <tr>
                  <th style={{ width: '220px' }}>Account</th>
                  <td>
                    {portfolio.Account.AccountNumber || <span className="text-muted">-</span>}
                    {' '}
                    <Badge color="info">{portfolio.Account.Status}</Badge>
                  </td>
                </tr>
                <tr>
                  <th>Equity</th>
                  <td>{formatMoney(portfolio.Account.Equity, portfolio.Account.Currency)}</td>
                </tr>
                <tr>
                  <th>Cash</th>
                  <td>{formatMoney(portfolio.Account.TradableCash, portfolio.Account.Currency)}</td>
                </tr>
                <tr>
                  <th>Buying Power</th>
                  <td>{formatMoney(portfolio.Account.BuyingPower, portfolio.Account.Currency)}</td>
                </tr>
                <tr>
                  <th>Long Market Value</th>
                  <td>{formatMoney(portfolio.Account.LongMarketValue, portfolio.Account.Currency)}</td>
                </tr>
                <tr>
                  <th>Short Market Value</th>
                  <td>{formatMoney(portfolio.Account.ShortMarketValue, portfolio.Account.Currency)}</td>
                </tr>
              </tbody>
            </table>
          )}

          {portfolio.Positions && portfolio.Positions.length > 0 ? (
            <Table striped responsive>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Side</th>
                  <th>Quantity</th>
                  <th>Avg Entry</th>
                  <th>Current</th>
                  <th>Market Value</th>
                  <th>Unrealized P/L</th>
                  <th>Unrealized %</th>
                  <th>Exchange</th>
                  <th>Class</th>
                </tr>
              </thead>
              <tbody>
                {portfolio.Positions.map((p) => (
                  <tr key={p.Symbol}>
                    <td><strong>{p.Symbol}</strong></td>
                    <td>{p.Side}</td>
                    <td>{p.Quantity}</td>
                    <td>{formatMoney(p.AverageEntryPrice)}</td>
                    <td>{formatMoney(p.AssetCurrentPrice)}</td>
                    <td>{formatMoney(p.MarketValue)}</td>
                    <td className={p.UnrealizedProfitLoss >= 0 ? 'text-success' : 'text-danger'}>
                      {formatMoney(p.UnrealizedProfitLoss)}
                    </td>
                    <td className={p.UnrealizedProfitLossPercent >= 0 ? 'text-success' : 'text-danger'}>
                      {formatPercent(p.UnrealizedProfitLossPercent)}
                    </td>
                    <td>{p.Exchange}</td>
                    <td>{p.AssetClass}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          ) : !portfolio.Account ? (
            // Account is unavailable — positions are typically also unavailable
            // in the same call, so show a contextual "can't load" note instead
            // of the generic "no open positions" message.
            <p className="text-muted">
              Open positions cannot be loaded while the broker account is unavailable.
            </p>
          ) : (
            <p className="text-muted">No open positions.</p>
          )}
        </>
      )}

      {/* Portfolio history chart (Equity + P&L over time, pulled from
          Alpaca via /api/trading-strategies/{id}/portfolio-history).
          Rendered inside the Portfolio section per the spec, but rendered
          even when the live portfolio snapshot failed — the chart fetches
          its own data so a temporary broker error on one endpoint
          shouldn't hide history the user explicitly asked for. */}
      <PortfolioHistoryChart strategyId={id} />

      <h2 className="h4 mt-4 mb-3">Open Orders</h2>

      {ordersError && <Alert color="danger">{ordersError}</Alert>}

      {ordersLoading && (
        <div className="text-center py-3"><Spinner /></div>
      )}

      {orders && !ordersLoading && (
        orders.length > 0 ? (
          <Table striped responsive>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Side</th>
                <th>Type</th>
                <th>Qty</th>
                <th>Filled</th>
                <th>Limit Price</th>
                <th>Avg Fill</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o) => (
                <tr key={o.OrderId}>
                  <td><strong>{o.Symbol}</strong></td>
                  <td className={o.Side === 'Buy' ? 'text-success' : 'text-danger'}>
                    <strong>{o.Side}</strong>
                  </td>
                  <td>{o.Type}</td>
                  <td>{formatQty(o.Quantity, o.IntegerQuantity)}</td>
                  <td>{formatQty(o.FilledQuantity, o.IntegerFilledQuantity)}</td>
                  <td>{formatMoney(o.LimitPrice)}</td>
                  <td>{formatMoney(o.AverageFillPrice)}</td>
                  <td><Badge color={orderStatusColor(o.Status)}>{o.Status}</Badge></td>
                  <td>{formatDateTime(o.SubmittedAtUtc) || formatDateTime(o.CreatedAtUtc)}</td>
                  <td>{formatDateTime(o.UpdatedAtUtc)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : portfolio && !portfolio.Account ? (
          // Broker is unavailable — don't tell the user "no open orders"
          // because we actually couldn't load them. The top-of-page banner
          // already explains the root cause; this is the contextual note
          // for this specific section.
          <p className="text-muted">
            Open orders cannot be loaded while the broker account is unavailable.
          </p>
        ) : (
          <p className="text-muted">No open orders.</p>
        )
      )}

      <h2 className="h4 mt-4 mb-3">Trade History</h2>
      <p className="text-muted small">Most recent 50 closed orders (filled, canceled, expired, etc.).</p>

      {tradeHistoryError && <Alert color="danger">{tradeHistoryError}</Alert>}

      {tradeHistoryLoading && (
        <div className="text-center py-3"><Spinner /></div>
      )}

      {tradeHistory && !tradeHistoryLoading && (
        tradeHistory.length > 0 ? (
          <Table striped responsive>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Side</th>
                <th>Type</th>
                <th>Qty</th>
                <th>Filled</th>
                <th>Limit Price</th>
                <th>Avg Fill</th>
                <th>Status</th>
                <th>Submitted</th>
                <th>Filled</th>
              </tr>
            </thead>
            <tbody>
              {tradeHistory.map((o) => (
                <tr key={o.OrderId}>
                  <td><strong>{o.Symbol}</strong></td>
                  <td className={o.Side === 'Buy' ? 'text-success' : 'text-danger'}>
                    <strong>{o.Side}</strong>
                  </td>
                  <td>{o.Type}</td>
                  <td>{formatQty(o.Quantity, o.IntegerQuantity)}</td>
                  <td>{formatQty(o.FilledQuantity, o.IntegerFilledQuantity)}</td>
                  <td>{formatMoney(o.LimitPrice)}</td>
                  <td>{formatMoney(o.AverageFillPrice)}</td>
                  <td><Badge color={orderStatusColor(o.Status)}>{o.Status}</Badge></td>
                  <td>{formatDateTime(o.SubmittedAtUtc) || formatDateTime(o.CreatedAtUtc)}</td>
                  <td>{formatDateTime(o.FilledAtUtc) || formatDateTime(o.CancelledAtUtc) || formatDateTime(o.ExpiredAtUtc)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : portfolio && !portfolio.Account ? (
          <p className="text-muted">
            Trade history cannot be loaded while the broker account is unavailable.
          </p>
        ) : (
          <p className="text-muted">No trade history yet.</p>
        )
      )}
    </div>
  );
};

function formatMoney(value, currency) {
  if (value === null || value === undefined) return <span className="text-muted">-</span>;
  const num = Number(value);
  if (Number.isNaN(num)) return <span className="text-muted">-</span>;
  return (
    <>
      {num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
      {currency ? ` ${currency}` : ''}
    </>
  );
}

function formatPercent(value) {
  if (value === null || value === undefined) return <span className="text-muted">-</span>;
  const num = Number(value);
  if (Number.isNaN(num)) return <span className="text-muted">-</span>;
  return `${(num * 100).toFixed(2)}%`;
}

// Stock shares and money-market allocations are stored as a percent value
// (50 means 50%, not 0.5). Use this formatter for them instead of
// `formatPercent`, which assumes a 0–1 fraction.
function formatStockShare(value) {
  if (value === null || value === undefined) return <span className="text-muted">-</span>;
  const num = Number(value);
  if (Number.isNaN(num)) return <span className="text-muted">-</span>;
  return `${num.toFixed(2)}%`;
}

function stockShareSum(strategy) {
  if (!strategy || !Array.isArray(strategy.Stocks)) return 0;
  return strategy.Stocks.reduce(
    (acc, s) => acc + (Number(s.Percentage) || 0),
    0
  );
}

function formatQty(fractional, integer) {
  // Prefer the fractional value for crypto / notional orders, fall back to the
  // integer-rounded quantity for share-based orders when fractional is null.
  if (fractional !== null && fractional !== undefined) {
    const num = Number(fractional);
    if (!Number.isNaN(num)) {
      return num.toLocaleString(undefined, { maximumFractionDigits: 4 });
    }
  }
  if (integer !== null && integer !== undefined) return String(integer);
  return <span className="text-muted">-</span>;
}

function formatDateTime(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString();
}

function orderStatusColor(status) {
  if (!status) return 'secondary';
  switch (status) {
    case 'Filled':
      return 'success';
    case 'PartiallyFilled':
      return 'info';
    case 'New':
    case 'Accepted':
    case 'PendingCancel':
      return 'primary';
    case 'Canceled':
    case 'Expired':
    case 'DoneForDay':
      return 'secondary';
    case 'Rejected':
    case 'Stopped':
    case 'Suspended':
      return 'danger';
    case 'Replaced':
      return 'warning';
    default:
      return 'secondary';
  }
}

function pendingActionColor(action) {
  if (!action) return 'secondary';
  switch ((action || '').toLowerCase()) {
    case 'buy': return 'success';
    case 'sell': return 'danger';
    case 'money': return 'info';
    default: return 'secondary';
  }
}
